import { useState } from "react";
import { Mail, Loader2, Check, BellRing } from "lucide-react";
import { toast } from "sonner";
import { http } from "@/lib/api";

const fmt = (d) => new Date(d).toLocaleDateString(undefined, { month: "long", day: "numeric", year: "numeric" });

export const EmailResultsCard = ({ token, savedEmail, reminderAt }) => {
  const [email, setEmail] = useState(savedEmail || "");
  const [reminder, setReminder] = useState(reminderAt);

  const remind = async (months) => {
    if (!email) { toast.error("Enter your email first"); return; }
    try {
      await http.put(`/results/${token}/email`, { email });
      const { data } = await http.put(`/results/${token}/reminder`, { months });
      setReminder(data.reminder_at);
      toast.success(months ? `We'll remind you on ${fmt(data.reminder_at)}.` : "Reminder cancelled.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not set the reminder");
    }
  };
  const [busy, setBusy] = useState(false);
  const [sentTo, setSentTo] = useState(null);

  const send = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await http.put(`/results/${token}/email`, { email });
      const { data } = await http.post(`/results/${token}/send-email`);
      setSentTo(data.to);
      toast.success(`Sent to ${data.to}. Check your inbox (and spam folder).`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not send the email. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rounded-2xl bg-white border border-[#1C242B]/10 p-6 sm:p-7" data-testid="email-results-card">
      <div className="flex items-center gap-2"><Mail size={18} className="text-[#D96B43]" /><p className="overline-label">Never lose your results</p></div>
      <p className="font-display text-xl font-semibold mt-3">Email yourself your private link &amp; PDF</p>
      <p className="text-sm text-slate-600 mt-1">We'll send your private results link and your report PDF (the AI version if unlocked). Your email is only used for this.</p>
      <form onSubmit={send} className="mt-5 flex flex-col sm:flex-row gap-3">
        <input data-testid="email-results-input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com"
          className="flex-1 rounded-full border border-[#1C242B]/15 px-5 py-3 text-sm bg-[#FAF8F5] focus:outline-none focus:ring-2 focus:ring-[#D96B43]/40" />
        <button data-testid="email-results-send-btn" disabled={busy || !email}
          className="inline-flex justify-center items-center gap-2 rounded-full bg-[#1C242B] text-white px-6 py-3 text-sm font-semibold hover:bg-[#D96B43] disabled:opacity-50 transition-colors">
          {busy ? <Loader2 size={16} className="animate-spin" /> : sentTo ? <Check size={16} /> : <Mail size={16} />} {sentTo ? "Send again" : "Email my results"}
        </button>
      </form>
      {sentTo && <p className="text-xs text-[#3E6B5C] mt-3" data-testid="email-results-confirmation">Sent to {sentTo}. Limit: 3 emails per day.</p>}
      <div className="mt-6 pt-5 border-t border-[#1C242B]/10" data-testid="reminder-controls">
        <p className="text-sm font-medium flex items-center gap-2"><BellRing size={15} className="text-[#3E6B5C]" /> Remind me to retake and check my goals in</p>
        <div className="mt-3 flex flex-wrap items-center gap-2">
          {[1, 3, 6].map((m) => (
            <button key={m} data-testid={`reminder-${m}m-btn`} onClick={() => remind(m)}
              className="rounded-full border border-[#1C242B]/15 bg-white px-4 py-1.5 text-sm hover:border-[#3E6B5C] hover:text-[#3E6B5C] transition-colors">
              {m} month{m > 1 ? "s" : ""}
            </button>
          ))}
          {reminder && <button data-testid="reminder-cancel-btn" onClick={() => remind(0)} className="text-xs underline text-slate-500 ml-1">Cancel reminder</button>}
        </div>
        {reminder && <p className="text-xs text-[#3E6B5C] mt-2" data-testid="reminder-status">Reminder set for {fmt(reminder)}. One friendly email, nothing else.</p>}
      </div>
    </div>
  );
};
